import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { Edit } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useHistory, useParams } from 'react-router-dom';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  ModalStyled,
  ModalHeaderStyled,
  ApplyClassesTextStyled,
  EmployeesCountStyled,
  ScrollAreaStyled,
  ClassButtonStyled,
  FooterButtonStyled,
  ModalFooterStyled,
  ApplyFooterButtonStyled,
} from './styles';
import { ClassesList } from './ClassesList';
import BatchGuidelines from '../BatchGuidelines';

interface ApplyClassesModalProps {
  show: boolean;
  onClose: () => void;
}

const ApplyClassesModal = observer(({ show, onClose }: ApplyClassesModalProps): JSX.Element => {
  const {
    employeeElectionsStore: {
      selectedSubscribersIds,
      employeeClasses,
      applyClassesAndResetProductSelection,
      updateElectionChanges,
      setProducts,
    },
    domainStoreV2: { availableClasses },
  } = useRootStore();

  const { periodId } = useParams<{ periodId: string }>();
  const query = useQueryParams();
  const groupId = query.get('groupId') || '';
  const history = useHistory();

  const [selectedClass, setSelectedClass] = useState<string>('');
  const convertedClasses = employeeClasses?.map((eeClass) => toJS(eeClass)).slice() ?? [];

  const isEditClassesAvailable = availableClasses.length === 0;
  const isApplyChangesAvailable = selectedSubscribersIds.length > 0;

  const onModalClose = (): void => {
    setSelectedClass('');
    onClose();
  };
  const editClasses = (): void => {
    history.push(`/ee-classes/${periodId}?groupId=${groupId}`);
    setProducts([]);
  };

  const applyChanges = (): void => {
    if (isApplyChangesAvailable) {
      applyClassesAndResetProductSelection(selectedClass);
      void updateElectionChanges(groupId);
    }
    onModalClose();
  };

  return (
    <ModalStyled $show={show}>
      {!isApplyChangesAvailable ? (
        <BatchGuidelines onClose={onModalClose} onAction={isEditClassesAvailable ? editClasses : undefined} />
      ) : (
        <>
          <ModalHeaderStyled>
            <ApplyClassesTextStyled>
              <span>Apply Class to </span>
              <EmployeesCountStyled>
                {`${selectedSubscribersIds.length} ${selectedSubscribersIds.length === 1 ? 'Employee' : 'Employees'}`}
              </EmployeesCountStyled>
            </ApplyClassesTextStyled>
            {isEditClassesAvailable ? (
              <ClassButtonStyled leftIconProp={<Edit />} type="text" color="primary" uppercase onClick={editClasses}>
                Edit classes
              </ClassButtonStyled>
            ) : (
              <Tooltip label="Not available for the current configuration" alignment={AlignmentTypes.BOTTOM_END}>
                <ClassButtonStyled leftIconProp={<Edit />} type="text" color="primary" uppercase disabled>
                  Edit classes
                </ClassButtonStyled>
              </Tooltip>
            )}
          </ModalHeaderStyled>
          <ScrollAreaStyled>
            <ClassesList
              classes={convertedClasses}
              selectedClass={selectedClass}
              availableClasses={availableClasses}
              onSelect={setSelectedClass}
            />
          </ScrollAreaStyled>
          <ModalFooterStyled>
            <FooterButtonStyled color="primary" type="outlined" onClick={onModalClose}>
              Cancel
            </FooterButtonStyled>
            <ApplyFooterButtonStyled color="primary" onClick={applyChanges} disabled={!selectedClass}>
              Apply
            </ApplyFooterButtonStyled>
          </ModalFooterStyled>
        </>
      )}
    </ModalStyled>
  );
});

export default ApplyClassesModal;
